import type {RepoIssuesDashboardContentResponse} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {connectionPathKey, type DocumentAdapter} from '@github-ui/optimistic-connection'
import type {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {type InfiniteData, useInfiniteQuery, useQueryClient} from '@github-ui/react-query'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {mergeDeferredIssues} from '../helpers/merge-deferred-issues'
import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'
import {
  milestoneIssuesQueryDeps,
  milestoneIssuesSecondaryQueryDeps,
  milestoneShowRoute,
} from '../routes/milestone-show-route'

type SecondaryPage = NonNullable<ReturnType<typeof useRouteQuery<typeof milestoneShowRoute, 'secondary'>>['data']>
type Page = {main: RepoIssuesDashboardContentResponse; secondary?: SecondaryPage; updatedAt: number}
export type MilestoneIssuesPagesData = InfiniteData<Page, number>

const MILESTONE_RESULTS_PATH = ['results']

export function milestoneIssuesPagesQueryKey({
  owner,
  repo,
  number,
  closed,
  requestedQuery,
}: {
  owner: string
  repo: string
  number: number
  closed: boolean
  requestedQuery: string | null
}) {
  return ['milestone-issues-pages', owner, repo, number, closed, requestedQuery] as const
}

export const milestoneIssuesPagesDocumentAdapter: DocumentAdapter<MilestoneIssuesPagesData> = (document, change) => {
  const operation = change.operation
  if (
    operation.type !== 'moveInConnection' ||
    connectionPathKey(operation.connectionPath) !== connectionPathKey(MILESTONE_RESULTS_PATH)
  ) {
    return undefined
  }

  const results = document.pages.flatMap(page => page.main.results)
  const from = results.findIndex(result => result.id === operation.nodeId)
  if (from === -1) return undefined

  const reordered = [...results]
  const [moved] = reordered.splice(from, 1)
  if (!moved) return undefined

  let insertIndex: number
  if ('at' in operation.anchor) {
    insertIndex = operation.anchor.at === 'start' ? 0 : reordered.length
  } else {
    const anchorId = 'before' in operation.anchor ? operation.anchor.before : operation.anchor.after
    const anchorIndex = reordered.findIndex(result => result.id === anchorId)
    if (anchorIndex === -1) return undefined
    insertIndex = 'before' in operation.anchor ? anchorIndex : anchorIndex + 1
  }
  reordered.splice(insertIndex, 0, moved)

  let offset = 0
  return {
    document: {
      ...document,
      pages: document.pages.map(page => {
        const nextResults = reordered.slice(offset, offset + page.main.results.length)
        offset += page.main.results.length
        return {...page, main: {...page.main, results: nextResults}}
      }),
    },
  }
}

export const MAX_MILESTONE_PAGE = 100

function isMainPage(value: unknown): value is RepoIssuesDashboardContentResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    '$typeName' in value &&
    value.$typeName === 'github.issues.v1.RepoIssuesDashboardContentResponse' &&
    'results' in value &&
    Array.isArray(value.results)
  )
}

function isSecondaryPage(value: unknown): value is SecondaryPage {
  if (typeof value !== 'object' || value === null || !('payload' in value)) return false
  const payload = value.payload
  if (typeof payload !== 'object' || payload === null || !('issuesIndexSecondaryData' in payload)) return false
  const secondary = payload.issuesIndexSecondaryData
  return (
    typeof secondary === 'object' && secondary !== null && 'results' in secondary && Array.isArray(secondary.results)
  )
}

export function normalizedMilestonePage(value: string | null): number {
  if (!value || !/^[1-9]\d*$/.test(value)) return 1
  return Math.min(Number(value), MAX_MILESTONE_PAGE)
}

export function useMilestoneIssuesPages({
  owner,
  repo,
  number,
  closed,
  requestedQuery,
  requestedPage,
  firstMain,
  firstSecondary,
  firstUpdatedAt,
  onBoundaryChange,
}: {
  owner: string
  repo: string
  number: number
  closed: boolean
  requestedQuery: string | null
  requestedPage: number
  firstMain: RepoIssuesDashboardContentResponse
  firstSecondary?: SecondaryPage
  firstUpdatedAt: number
  onBoundaryChange: (page: number) => void
}) {
  const queryClient = useQueryClient()
  const queryKey = useMemo(
    () => milestoneIssuesPagesQueryKey({owner, repo, number, closed, requestedQuery}),
    [owner, repo, number, closed, requestedQuery],
  )
  const operationRef = useRef(0)
  const pendingRef = useRef(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorKind, setErrorKind] = useState<'append' | 'refresh'>('append')

  const keys = useCallback(
    (page: number) => {
      const secondary = milestoneShowRoute.queries.secondary
      if (!secondary) throw new Error('Missing milestone deferred query')
      return {
        main: milestoneShowRoute.queries.mainQuery.makeQueryKey(
          milestoneIssuesQueryDeps(owner, repo, number, closed, page, requestedQuery),
        ),
        secondary: secondary.makeQueryKey(
          milestoneIssuesSecondaryQueryDeps(owner, repo, number, closed, page, requestedQuery),
        ),
      }
    },
    [owner, repo, number, closed, requestedQuery],
  )

  const readPage = useCallback(
    async (page: number): Promise<Page> => {
      const {main: mainKey, secondary: secondaryKey} = keys(page)
      const mainOptions = milestoneShowRoute.buildQueryOptions('mainQuery', mainKey)
      const secondaryOptions = milestoneShowRoute.buildQueryOptions('secondary', secondaryKey)
      const [rawMain, secondary] = await Promise.all([
        queryClient.fetchQuery({...mainOptions, staleTime: 0}),
        queryClient.fetchQuery({...secondaryOptions, staleTime: 0}),
      ])
      const main = mainOptions.select ? mainOptions.select(rawMain) : rawMain
      if (!isMainPage(main)) throw new Error('Invalid milestone issues response')
      if (!isSecondaryPage(secondary)) throw new Error('Invalid milestone deferred response')
      if (main.error) throw new Error(main.error)
      return {
        main,
        secondary,
        updatedAt: Math.max(
          queryClient.getQueryState(mainKey)?.dataUpdatedAt ?? 0,
          queryClient.getQueryState(secondaryKey)?.dataUpdatedAt ?? 0,
        ),
      }
    },
    [keys, queryClient],
  )

  const {
    data: queryData,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({pageParam}) => readPage(pageParam),
    initialPageParam: 1,
    initialData: {
      pages: [{main: firstMain, secondary: firstSecondary, updatedAt: firstUpdatedAt}],
      pageParams: [1],
    },
    initialDataUpdatedAt: firstUpdatedAt,
    getNextPageParam: (lastPage, pages) =>
      pages.length < (lastPage.main.pageInfo?.totalPages ?? 1) ? pages.length + 1 : undefined,
    enabled: false,
    retry: false,
  })

  const extend = useCallback(
    async (target: number, updateUrl: boolean) => {
      const current = queryClient.getQueryData<InfiniteData<Page, number>>(queryKey)
      if (pendingRef.current || !current?.pages[0]?.secondary) return false
      const operation = ++operationRef.current
      pendingRef.current = true
      setIsLoading(true)
      setError(null)
      try {
        let pages = current.pages
        while (pages.length < target && pages.length < (pages.at(-1)?.main.pageInfo?.totalPages ?? 1)) {
          const result = await fetchNextPage({cancelRefetch: false})
          if (operation !== operationRef.current) return false
          if (result.isError) throw result.error ?? new Error('Unable to load more issues.')
          if (!result.data || result.data.pages.length <= pages.length) throw new Error('Unable to load more issues.')
          pages = result.data.pages
        }
        if (operation === operationRef.current && (updateUrl || pages.length < target)) {
          onBoundaryChange(pages.length)
        }
        return true
      } catch (caught) {
        if (operation === operationRef.current) {
          setError(caught instanceof Error ? caught.message : 'Unable to load more issues.')
          setErrorKind('append')
        }
        return false
      } finally {
        if (operation === operationRef.current) {
          pendingRef.current = false
          setIsLoading(false)
        }
      }
    },
    [fetchNextPage, onBoundaryChange, queryClient, queryKey],
  )

  useEffect(() => {
    if (!firstSecondary) return
    queryClient.setQueryData<InfiniteData<Page, number>>(queryKey, current => {
      const firstPage = current?.pages[0]
      if (!firstPage || firstPage.secondary) return current
      return {...current, pages: [{...firstPage, secondary: firstSecondary}, ...current.pages.slice(1)]}
    })
  }, [firstSecondary, queryClient, queryKey])

  useEffect(() => {
    if (queryData.pages.length < requestedPage && !pendingRef.current && !error && !isLoading) {
      void extend(requestedPage, false)
    }
  }, [extend, requestedPage, queryData.pages.length, firstSecondary, error, isLoading])

  useEffect(
    () => () => {
      operationRef.current++
      void queryClient.cancelQueries({queryKey, exact: true})
      const loadedPages = queryClient.getQueryData<InfiniteData<Page, number>>(queryKey)?.pages.length ?? 1
      for (let page = 1; page <= loadedPages + 1; page++) {
        const {main, secondary} = keys(page)
        void queryClient.cancelQueries({queryKey: main, exact: true})
        void queryClient.cancelQueries({queryKey: secondary, exact: true})
      }
    },
    [keys, queryClient, queryKey],
  )

  const refresh = useCallback(async () => {
    const operation = ++operationRef.current
    pendingRef.current = true
    setIsLoading(true)
    setIsRefreshing(true)
    setError(null)
    const boundary = queryData.pages.length
    try {
      await queryClient.cancelQueries({queryKey, exact: true})
      await Promise.all(
        Array.from({length: boundary + 1}, (_, index) => {
          const {main, secondary} = keys(index + 1)
          return Promise.all([
            queryClient.cancelQueries({queryKey: main, exact: true}),
            queryClient.cancelQueries({queryKey: secondary, exact: true}),
          ])
        }),
      )
      const result = await refetch()
      if (operation !== operationRef.current) return false
      if (result.isError) throw result.error ?? new Error('Unable to refresh issues.')
      if (!result.data?.pages.length) throw new Error('Unable to refresh issues.')
      if (result.data.pages.length < requestedPage) onBoundaryChange(result.data.pages.length)
      return true
    } catch (caught) {
      if (operation === operationRef.current) {
        setError(caught instanceof Error ? caught.message : 'Unable to refresh issues.')
        setErrorKind('refresh')
      }
      return false
    } finally {
      if (operation === operationRef.current) {
        pendingRef.current = false
        setIsLoading(false)
        setIsRefreshing(false)
      }
    }
  }, [keys, onBoundaryChange, queryData.pages.length, queryClient, queryKey, refetch, requestedPage])
  const missingRowRefreshRef = useRef<Promise<boolean> | null>(null)
  const reconcileMissingRow = useCallback(() => {
    const current = missingRowRefreshRef.current
    if (current) return current

    const revalidation = refresh()
    missingRowRefreshRef.current = revalidation
    void revalidation.finally(() => {
      if (missingRowRefreshRef.current === revalidation) missingRowRefreshRef.current = null
    })
    return revalidation
  }, [refresh])

  const visiblePages = useMemo(() => queryData.pages.slice(0, requestedPage), [requestedPage, queryData.pages])
  const rows = useMemo(() => {
    const seen = new Set<string>()
    return visiblePages.flatMap(({main, secondary}) =>
      mergeDeferredIssues(
        repoIssuesToListItems(main.results),
        secondary?.payload?.issuesIndexSecondaryData?.results ?? [],
      ).filter(row => {
        const identity = getListItemIdentity(row)
        if (seen.has(identity)) return false
        seen.add(identity)
        return true
      }),
    )
  }, [visiblePages])
  const rowUpdatedAt = useMemo(() => {
    const timestamps = new Map<string, number>()
    for (const {main, updatedAt} of visiblePages) {
      for (const row of repoIssuesToListItems(main.results)) {
        const identity = getListItemIdentity(row)
        if (!timestamps.has(identity)) timestamps.set(identity, updatedAt)
      }
    }
    return timestamps
  }, [visiblePages])
  const lastPage = visiblePages.at(-1)

  return {
    rows,
    rowUpdatedAt,
    lastPage,
    currentPage: visiblePages.length,
    hasMore: visiblePages.length < (lastPage?.main.pageInfo?.totalPages ?? 1),
    isLoading,
    isRefreshing,
    error,
    errorKind,
    loadMore: () => extend(visiblePages.length + 1, true),
    refresh,
    reconcileMissingRow,
  }
}
